"use client";

import type { UpdateBookingConstraintsRequest } from "@/features/calendar/types/schedule-settings";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatDuration } from "date-fns";
import i18n from "i18next"; // Direct import for formatDuration
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { InformationTooltip } from "@/components/InformationTooltip.tsx";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";

const timeConstraintsSchema = z.object({
  bookingTime: z.number().min(2).max(48),
  cancellationTime: z.number().min(6).max(72),
});

type TimeConstraintsFormValues = z.infer<typeof timeConstraintsSchema>;

interface TimeSettingsCardProps {
  bookingTime: number;
  cancellationTime: number;
  onSave: (data: UpdateBookingConstraintsRequest) => Promise<void>;
  disabled?: boolean;
  isSaving?: boolean;
}

export function TimeSettingsCard({
  bookingTime,
  cancellationTime,
  onSave,
  disabled = false,
  isSaving = false,
}: TimeSettingsCardProps) {
  const { t } = useTranslation(["calendar", "common"]);

  const form = useForm<TimeConstraintsFormValues>({
    resolver: zodResolver(timeConstraintsSchema),
    defaultValues: {
      bookingTime,
      cancellationTime,
    },
    mode: "onChange",
  });

  const isDirty = form.formState.isDirty;

  useEffect(() => {
    form.reset({
      bookingTime,
      cancellationTime,
    });
  }, [bookingTime, cancellationTime, form]);

  const onSubmit = async (data: TimeConstraintsFormValues) => {
    await onSave({
      min_time_before_booking_lesson: data.bookingTime,
      min_time_before_cancelling_lesson_without_provision: data.cancellationTime,
    });
  };

  return (
    <div className="bg-foreground/50 text-accent-foreground relative rounded-2xl border-none p-8 shadow-2xl backdrop-blur-xl max-[400px]:p-2">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden rounded-2xl opacity-5">
        {/* Changed colors and position for the first circle */}
        <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br from-cyan-400 to-teal-600" />
        {/* Changed colors and position for the second circle */}
        <div className="absolute -bottom-24 -left-16 h-36 w-36 rounded-full bg-gradient-to-tr from-purple-400 to-indigo-500" />
      </div>

      <div className="relative">
        <Form {...form}>
          <form className="space-y-6" onSubmit={() => onSubmit(form.getValues())}>
            <div className="space-y-6">
              {/* Booking Time Setting */}
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-4">
                    <div className="bg-muted/20 flex h-14 w-14 items-center justify-center rounded-2xl max-sm:bg-transparent min-sm:backdrop-blur-sm">
                      <Icon
                        className="text-accent-foreground"
                        height={"1.75rem"}
                        icon="mdi:alarm-clock-add"
                        width={"1.75rem"}
                      />
                    </div>
                    <div className="flex flex-col">
                      <h3 className="text-accent-foreground text-xl font-semibold">
                        {t("calendar:settings.bookingTime.title")}
                      </h3>
                      <p className="text-muted-foreground mt-1 text-sm">
                        {t("calendar:settings.bookingTime.description")}
                      </p>
                    </div>
                  </div>
                  <InformationTooltip
                    description={t("calendar:settings.bookingTime.tooltipDescription")}
                    iconSize="1.25rem"
                  />
                </div>

                <FormField
                  control={form.control}
                  name="bookingTime"
                  render={({ field }) => (
                    <FormItem className="pt-2">
                      <FormControl>
                        <div className="bg-muted/20 rounded-2xl p-6 backdrop-blur-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground text-xs">
                              {t("calendar:settings.currentValue")}
                            </span>
                            <AnimatePresence mode="wait">
                              <motion.span
                                key={`booking-${field.value}`}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-accent-foreground text-base font-medium"
                                exit={{ opacity: 0, y: 5 }}
                                initial={{ opacity: 0, y: -5 }}
                                transition={{ duration: 0.2 }}
                              >
                                {formatDuration(
                                  { hours: field.value },
                                  { locale: getDateFnsLocale(i18n.language) },
                                )}
                              </motion.span>
                            </AnimatePresence>
                          </div>

                          <Slider
                            className="py-6"
                            disabled={disabled || isSaving}
                            max={48}
                            min={2}
                            sliderClassName={"bg-accent-foreground/75"}
                            step={1}
                            thumbClassName={"bg-accent-foreground ring-accent-foreground/50"}
                            value={[field.value]}
                            onValueChange={(values) => field.onChange(values[0])}
                          />

                          <div className="flex justify-between">
                            <span className="text-muted-foreground text-xs">
                              {formatDuration(
                                { hours: 2 },
                                { locale: getDateFnsLocale(i18n.language) },
                              )}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              {formatDuration(
                                { hours: 48 },
                                { locale: getDateFnsLocale(i18n.language) },
                              )}
                            </span>
                          </div>
                        </div>
                      </FormControl>
                      <AnimatePresence>
                        {form.formState.errors.bookingTime && (
                          <motion.div
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            initial={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <FormMessage />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </FormItem>
                  )}
                />
              </div>

              <Separator className="bg-border/20" />

              {/* Cancellation Time Setting */}
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-4">
                    <div className="bg-muted/20 flex h-14 w-14 items-center justify-center rounded-2xl max-sm:bg-transparent min-sm:backdrop-blur-sm">
                      <Icon
                        className="text-accent-foreground"
                        height={"1.75rem"}
                        icon="mdi:calendar-remove"
                        width={"1.75rem"}
                      />
                    </div>
                    <div className="flex flex-col">
                      <h3 className="text-accent-foreground text-xl font-semibold">
                        {t("calendar:settings.cancellationTime.title")}
                      </h3>
                      <p className="text-muted-foreground mt-1 text-sm">
                        {t("calendar:settings.cancellationTime.description")}
                      </p>
                    </div>
                  </div>
                  <InformationTooltip
                    description={t("calendar:settings.cancellationTime.tooltipDescription")}
                    iconSize="1.25rem"
                  />
                </div>

                <FormField
                  control={form.control}
                  name="cancellationTime"
                  render={({ field }) => (
                    <FormItem className="pt-2">
                      <FormControl>
                        <div className="bg-muted/20 rounded-2xl p-6 backdrop-blur-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground text-xs">
                              {t("calendar:settings.currentValue")}
                            </span>
                            <AnimatePresence mode="wait">
                              <motion.span
                                key={`cancel-${field.value}`}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-accent-foreground text-base font-medium"
                                exit={{ opacity: 0, y: 5 }}
                                initial={{ opacity: 0, y: -5 }}
                                transition={{ duration: 0.2 }}
                              >
                                {formatDuration(
                                  { hours: field.value },
                                  { locale: getDateFnsLocale(i18n.language) },
                                )}
                              </motion.span>
                            </AnimatePresence>
                          </div>

                          <Slider
                            className="py-6"
                            disabled={disabled || isSaving}
                            max={72}
                            min={6}
                            sliderClassName={"bg-accent-foreground/75"}
                            step={1}
                            thumbClassName={"bg-accent-foreground ring-accent-foreground/50"}
                            value={[field.value]}
                            onValueChange={(values) => field.onChange(values[0])}
                          />

                          <div className="flex justify-between">
                            <span className="text-muted-foreground text-xs">
                              {formatDuration(
                                { hours: 6 },
                                { locale: getDateFnsLocale(i18n.language) },
                              )}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              {formatDuration(
                                { hours: 72 },
                                { locale: getDateFnsLocale(i18n.language) },
                              )}
                            </span>
                          </div>
                        </div>
                      </FormControl>
                      <AnimatePresence>
                        {form.formState.errors.cancellationTime && (
                          <motion.div
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            initial={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <FormMessage />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </form>
        </Form>
        <AnimatePresence>
          {isDirty && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-end pt-2"
              exit={{ opacity: 0, y: 10 }}
              initial={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
            >
              <Button
                className="gap-1.5"
                disabled={disabled || isSaving || !form.formState.isValid}
                size="sm"
                type={"button"}
                variant="foreground"
                onClick={() => onSubmit(form.getValues())}
              >
                {isSaving ? (
                  <Icon className="animate-spin" height="1rem" icon="mdi:loading" width="1rem" />
                ) : (
                  <Icon height="1rem" icon="mdi:content-save" width="1rem" />
                )}
                {t("common:save")}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
